import { useEffect, useRef } from "react";

import wsManager, {
  CreateConnectionOptions,
  EventHandlers
} from "../WebSocketManager";

type BaseProps = Omit<CreateConnectionOptions, "userId" | "sessionId"> &
  EventHandlers;

type UseWebSocketProps = BaseProps &
  (
    | { enabled: true; userId: string; sessionId: string }
    | { enabled: false; userId?: string | null; sessionId?: string | null }
  );

function useWebSocketImpl(props: UseWebSocketProps) {
  const { userId, sessionId, timeoutMs, enabled, ...eventHandlers } = props;

  useEffect(() => {
    eventHandlersRef.current = eventHandlers;
  }, [eventHandlers]);

  const eventHandlersRef = useRef(eventHandlers);

  useEffect(() => {
    if (enabled) {
      (async () => {
        await wsManager.getOrCreateConnection({
          userId,
          sessionId,
          timeoutMs,
          eventHandlers: eventHandlersRef.current
        });
      })();

      return () => wsManager.closeConnection(sessionId);
    }
  }, [enabled, userId, sessionId, timeoutMs]);
}

type Props = BaseProps & {
  userId?: string | null;
  sessionId?: string | null;
};

function getProps({ userId, sessionId, ...props }: Props): UseWebSocketProps {
  if (!userId || !sessionId) return { ...props, enabled: false };
  return { ...props, userId, sessionId, enabled: true };
}

export default function useWebSocket(props: Props) {
  return useWebSocketImpl(getProps(props));
}
